import {
  ExperimentOutlined,
  FileTextOutlined,
  ImportOutlined,
  ExportOutlined,
  CreditCardOutlined,
  DollarOutlined,
  SafetyCertificateOutlined,
  CrownOutlined,
  UserOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import { ActivityLog, ActivityLogRaw, Department, UserType } from './ActivityLog.types';

// ─── Department config ────────────────────────────────────────────────────────
export const DEPARTMENT_CONFIG: Record<Department, { iconKey: string; color: string; bg: string; label: string }> = {
  LabRequest: { iconKey: 'experiment', color: '#0C447C', bg: '#E6F1FB', label: 'VRI' },
  LabRequestServiceCategory: { iconKey: 'experiment', color: '#0C447C', bg: '#E6F1FB', label: 'VRI' },
  ProductRegistration: { iconKey: 'fileText', color: '#3B6D11', bg: '#EAF3DE', label: 'Product Registration' },
  Import: { iconKey: 'import', color: '#854F0B', bg: '#FAEEDA', label: 'Import' },
  Export: { iconKey: 'export', color: '#533AB7', bg: '#EEEDFE', label: 'Export' },
  NominalPayment: { iconKey: 'creditCard', color: '#993C1D', bg: '#FAECE7', label: 'Nominal Payments' },
  Invoice: { iconKey: 'dollar', color: '#085041', bg: '#E1F5EE', label: 'Finance' },
};

// ─── User type config ─────────────────────────────────────────────────────────
export const USER_TYPE_CONFIG: Record<UserType, { iconKey: string; color: string; bg: string; label: string }> = {
  ADMIN: { iconKey: 'safety', color: '#533AB7', bg: '#EEEDFE', label: 'Admin' },
  SUPERADMIN: { iconKey: 'crown', color: '#3B6D11', bg: '#EAF3DE', label: 'Super Admin' },
  CUSTOMER: { iconKey: 'user', color: '#185FA5', bg: '#E6F1FB', label: 'Customer' },
  CASHIER: { iconKey: 'audit', color: '#854F0B', bg: '#FAEEDA', label: 'Cashier' },
  LAB_TECHNICIAN: { iconKey: 'experiment', color: '#0F6E56', bg: '#E1F5EE', label: 'Lab Technician' },
};

export const STATUS_CONFIG = {
  success: { color: '#3B6D11', bg: '#EAF3DE', label: 'Success' },
  warning: { color: '#854F0B', bg: '#FAEEDA', label: 'Warning' },
  error: { color: '#A32D2D', bg: '#FCEBEB', label: 'Error' },
  info: { color: '#185FA5', bg: '#E6F1FB', label: 'Info' },
};

// ─── Icon renderer ────────────────────────────────────────────────────────────
// Returns the correct AntD icon component for a given iconKey
export function getIcon(iconKey: string, style?: React.CSSProperties): React.ReactNode {
  const props = { style };
  switch (iconKey) {
    case 'experiment':
      return <ExperimentOutlined {...props} />;
    case 'fileText':
      return <FileTextOutlined {...props} />;
    case 'import':
      return <ImportOutlined {...props} />;
    case 'export':
      return <ExportOutlined {...props} />;
    case 'creditCard':
      return <CreditCardOutlined {...props} />;
    case 'dollar':
      return <DollarOutlined {...props} />;
    case 'safety':
      return <SafetyCertificateOutlined {...props} />;
    case 'crown':
      return <CrownOutlined {...props} />;
    case 'user':
      return <UserOutlined {...props} />;
    case 'audit':
      return <AuditOutlined {...props} />;
    default:
      return <UserOutlined {...props} />;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
export function formatDateTime(iso: string) {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  };
}

export function getUserInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function applyFilters(
  logs: ActivityLog[],
  filters: {
    department?: string;
    userType?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  },
): ActivityLog[] {
  let result = [...logs];
  if (filters.department) result = result.filter((l) => l.department === filters.department);
  if (filters.userType) result = result.filter((l) => l.userType === filters.userType);
  if (filters.status) result = result.filter((l) => l.status === filters.status);
  if (filters.dateFrom) result = result.filter((l) => new Date(l.timestamp) >= new Date(filters.dateFrom!));
  if (filters.dateTo) result = result.filter((l) => new Date(l.timestamp) <= new Date(filters.dateTo! + 'T23:59:59Z'));
  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (l) =>
        l.userName.toLowerCase().includes(q) ||
        l.description.toLowerCase().includes(q) ||
        l.action.toLowerCase().includes(q) ||
        l.affectedEntity?.toLowerCase().includes(q),
    );
  }
  const sortBy = (filters.sortBy as keyof ActivityLog) || 'timestamp';
  const sortDir = filters.sortOrder || 'desc';
  result.sort((a, b) => {
    const av = String(a[sortBy]);
    const bv = String(b[sortBy]);
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  return result;
}

function deriveStatus(activity: string): ActivityLog['status'] {
  const a = activity.toUpperCase();
  if (a.includes('DELETE') || a.includes('REJECT') || a.includes('FAIL') || a.includes('ERROR')) return 'error';
  if (a.includes('PENDING') || a.includes('SUSPEND') || a.includes('OVERDUE') || a.includes('WARN')) return 'warning';
  if (a.includes('CREATE') || a.includes('APPROVE') || a.includes('COMPLETE') || a.includes('GENERATE'))
    return 'success';
  return 'info';
}

// Formats activity string → readable action title
function formatActivity(activity: string): string {
  return activity
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Extracts a readable entity from subject_type (e.g. "App\Models\ExportExtension" → "ExportExtension #17")
function formatEntity(module: string, subjectId: number): string {
  const model = module.split('\\').pop() || module;
  return `${model} #${subjectId ?? ''}`;
}

// Parses changes JSON into metadata key-value pairs
function parseChanges(changes: string): Record<string, string | number> {
  try {
    const parsed = JSON.parse(changes);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const meta: Record<string, string | number> = {};
    for (const [field, val] of Object.entries(parsed)) {
      if (val && typeof val === 'object' && 'old' in (val as object) && 'new' in (val as object)) {
        const v = val as { old: string; new: string };
        meta[field] = `${v.old} → ${v.new}`;
      } else {
        meta[field] = String(val);
      }
    }
    return meta;
  } catch {
    return {};
  }
}

export function mapRawToActivityLog(raw: ActivityLogRaw): ActivityLog {
  console.log('raw.module:', raw.module, '| raw.usertype:', raw.usertype);
  return {
    id: raw.id,
    timestamp: raw.created_at,
    department: (raw.module as Department) || 'Export',
    userType: (raw.usertype as UserType) || 'CUSTOMER',
    userName: raw.username || raw.email || `User #${raw.user_id}`,
    action: formatActivity(raw.activity),
    description: `${formatActivity(raw.activity)} on ${formatEntity(raw.module, raw.subject_id)}`,
    metadata: parseChanges(raw.changes),
    ipAddress: raw.ip,
    affectedEntity: formatEntity(raw.module, raw.subject_id),
    status: deriveStatus(raw.activity),
    changes: raw.changes,
    module: raw.module,
  };
}
